import { useState } from "react";
import { Info } from "lucide-react";

interface FalseVParam {
  level: string;
  value: number;
}

interface Props {
  falseV: {
    smc: FalseVParam;
    msf: FalseVParam;
    ifr: FalseVParam;
    k: FalseVParam;
  };
}

// D-1: reframed from "Goal: 0" (physically and clinically wrong — a living
// system with zero friction has no structure) to friction *redistribution*:
// some cost is load-bearing, the question is where it sits.
const PARAM_INFO = {
  smc: {
    label: "SMC — Structural Mode Count",
    description: "Information gap between parallel tasks/identities. More modes with greater disparity = harder to see core differences. High SMC blocks o1 (difference recognition). Healthy range: enough modes to stay adaptable, few enough to still see what they share.",
  },
  msf: {
    label: "MSF — Mode Switching Frequency",
    description: "How frequently you switch between tasks/modes. Excessive switching prevents stabilization, breaking the adiabatic approximation. High MSF blocks o2 (pause & stabilize). Healthy range: switching that follows your own rhythm rather than being pulled by interruptions.",
  },
  ifr: {
    label: "SRF — Self-Representation Friction",
    description: "Energy cost of maintaining inconsistency between your internal self-model and external self-presentation — social masks, role conflicts, suppressed preferences, context-switching between incompatible presentations. High SRF blocks o3 (new paradigm decision). Healthy range: contextual adaptation that stays continuous with who you actually are.",
  },
  k: {
    label: "ECL — Effective Cognitive Load",
    description: "Unresolved processing load currently occupying the system — not objective task complexity, but the gap between task demands and current mastery / flow. A rocket engineer in flow has low ECL; an overwhelmed person answering email has high ECL. Healthy range: demand slightly above current mastery — enough to engage, not enough to overwhelm.",
  },
};


/** Inverted color: lower = better (green), higher = worse (red) */
function getBarColor(value: number): string {
  if (value <= 15) return "bg-emerald-400";
  if (value <= 30) return "bg-green-400";
  if (value <= 50) return "bg-amber-400";
  if (value <= 70) return "bg-orange-500";
  return "bg-red-500";
}

export default function FalseVPanel({ falseV }: Props) {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  return (
    <div className="space-y-2 md:space-y-3">
      <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
        <svg className="h-4 w-4 md:h-5 md:w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        Where Your Effort Goes
      </h3>
      <p className="text-xs md:text-sm text-muted-foreground">
        Four structural places friction accumulates. The aim isn’t zero friction — some of it holds you together. The aim is moving cost off the parts that are draining you.
      </p>


      <div className="space-y-3 md:space-y-4">
        {(["smc", "msf", "ifr", "k"] as const).map((key) => {
          const param = falseV[key];
          const info = PARAM_INFO[key];
          const isOpen = openTooltip === key;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1 md:gap-1.5">
                  <span className="text-xs md:text-sm font-semibold text-foreground">{info.label}</span>
                  <button
                    type="button"
                    className="relative"
                    onClick={() => setOpenTooltip(isOpen ? null : key)}
                    aria-label={`Info about ${key.toUpperCase()}`}
                  >
                    <Info className="h-3 w-3 md:h-3.5 md:w-3.5 text-muted-foreground" />
                    {isOpen && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-3 py-2 bg-foreground text-background text-[10px] md:text-xs rounded-md w-[300px] md:w-[360px] z-20 leading-relaxed shadow-lg">
                        {info.description}
                      </div>
                    )}
                  </button>
                </div>
                <span className="text-[10px] md:text-xs font-medium px-1.5 md:px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {param.level}
                </span>
              </div>
              <div className="h-2.5 md:h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getBarColor(param.value)}`}
                  style={{ width: `${param.value}%` }}
                />
              </div>
              <span className="text-[10px] md:text-xs text-muted-foreground">{param.value}/100 (goal: 0)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
